// src/components/weeklyReport/StudyModals.js
//
// The study steps of the weekly report:
//   StudyPickerModal   pick a study to report on
//   StudyPatientsModal that study's patients this week (+ suggestions)
//   AnotherStudyModal  "Add another study?" after finishing one

import { useMemo, useState } from "react";
import { FlaskConical, Search, Users, Plus, Pencil, Trash2, Sparkles, ClipboardCheck } from "lucide-react";
import { WrModal, BTN_PRIMARY, BTN_SOFT, BTN_SMALL, INPUT, LABEL, StatusChip } from "./ui";
import { REPORT_STATUSES, studyTotals, statusLabel } from "../../services/weeklyReportModel";

const studyTitle = (s) => `${s.STUDY_ID || s.studyId ? `${s.STUDY_ID || s.studyId} – ` : ""}${s.NAME || s.name || "(unnamed study)"}`;

/**
 * Count chips for one study: pass the study entry (people + follow-ups not
 * listed by name) or just a patient list.
 */
export const TotalsChips = ({ study, patients }) => {
  const t = studyTotals(study || patients || []);
  return (
    <div className="flex flex-wrap gap-1.5">
      {REPORT_STATUSES.map((s) => (
        <StatusChip key={s.id} status={t[s.field] ? s.id : "none"}>
          {s.label}: {t[s.field]}
        </StatusChip>
      ))}
    </div>
  );
};

export const StudyPickerModal = ({ open, studies, alreadyAdded = [], onPick, onClose }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return studies.filter(
      (s) =>
        !needle ||
        String(s.STUDY_ID || "").toLowerCase().includes(needle) ||
        String(s.NAME || "").toLowerCase().includes(needle)
    );
  }, [studies, q]);

  return (
    <WrModal open={open} onClose={onClose} icon={FlaskConical} title="Pick a study" subtitle="Active and Active/NR studies">
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by Study ID or name…"
          className={`${INPUT} pl-9`}
          autoFocus
        />
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">No studies match.</p>
      ) : (
        <ul className="space-y-1.5">
          {shown.map((s) => {
            const added = alreadyAdded.includes(s.id);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onPick(s)}
                  className="weekly-report-result w-full text-left flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-white truncate">{studyTitle(s)}</span>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-400">{s.STATUS}</span>
                  </span>
                  <span className="text-[11px] text-slate-400 shrink-0">{added ? "In report – open" : "Add →"}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </WrModal>
  );
};

export const StudyPatientsModal = ({
  open,
  study,
  suggestions = [],
  onFollowUpCountChange,
  onAddPatient,
  onEditPatient,
  onRemovePatient,
  onQuickAdd,
  onDone,
  onClose,
}) => {
  if (!study) return null;
  const patients = study.patients || [];
  return (
    <WrModal
      open={open}
      onClose={onClose}
      wide
      icon={Users}
      title={studyTitle(study)}
      subtitle="People you worked with, and follow-ups"
      footer={
        <button type="button" onClick={onDone} className={BTN_PRIMARY}>
          <ClipboardCheck className="w-4 h-4" />
          Done with this study
        </button>
      }
    >
      <TotalsChips study={study} />

      <div className="weekly-report-row mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3">
        <label className="block max-w-xs">
          <span className={LABEL}>Follow-ups not listed by name</span>
          <input
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={study.followUpCount ?? ""}
            onChange={(e) => onFollowUpCountChange?.(e.target.value)}
            className={INPUT}
            placeholder="0"
            aria-label="Follow-ups not listed by name"
          />
        </label>
        <p className="mt-1 text-[11px] text-slate-400">
          Just a number for this study. Don't count people you add below as Follow-up; they're counted already.
        </p>
      </div>

      <div className="mt-4">
        {patients.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4 rounded-xl border border-dashed border-white/10">
            No people added yet. Click "Add a patient" below, or just enter a follow-up number above.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {patients.map((p) => (
              <li
                key={p.key}
                className="weekly-report-row flex items-center gap-3 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.03]"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">
                    {p.name || "(no name)"}
                    {p.notInRegx && <span className="ml-2 text-[10px] font-medium text-amber-300">not in RegX</span>}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    MRN {p.mrn || "—"} · DOB {p.dob || "—"}
                    {p.subjectNumber ? ` · Subject ${p.subjectNumber}` : ""}
                  </div>
                </div>
                <StatusChip status={p.status} />
                <button type="button" className={BTN_SMALL} onClick={() => onEditPatient(p)} aria-label="Edit patient">
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  className={BTN_SMALL}
                  onClick={() => {
                    if (window.confirm(`Remove ${p.name || "this patient"} from this study's list?`)) onRemovePatient(p.key);
                  }}
                  aria-label="Remove patient"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Big and in the middle so it's clear what to click (2026-10-09). */}
      <div className="mt-4 flex justify-center">
        <button type="button" onClick={onAddPatient} className={`${BTN_PRIMARY} !px-8 !py-3 !text-base`}>
          <Plus className="w-5 h-5" />
          Add {patients.length ? "another" : "a"} patient
        </button>
      </div>

      {suggestions.length > 0 && (
        <section
          className="weekly-report-suggest mt-5 rounded-xl border border-dashed border-sky-400/50 bg-sky-500/10 p-3"
          aria-label="Suggestions from your earlier reports"
        >
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <Sparkles className="weekly-report-suggest-icon w-3.5 h-3.5 text-sky-300" />
            <span className="weekly-report-suggest-title text-[11px] uppercase tracking-wider font-semibold text-sky-200">
              Suggestions
            </span>
            <span className="weekly-report-suggest-note text-[11px] text-sky-200/80">
              · not added yet. From your earlier reports. Tap a status to add.
            </span>
          </div>
          <ul className="space-y-1.5">
            {suggestions.map((s) => (
              <li
                key={`${s.entry.patientRef ? `${s.entry.patientRef.source}:${s.entry.patientRef.id}` : `${s.entry.name}|${s.entry.dob}`}`}
                className="weekly-report-suggest-row flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-sky-400/20 bg-sky-500/[0.06]"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">{s.entry.name || "(no name)"}</div>
                  <div className="text-[11px] text-slate-400">
                    Last reported {statusLabel(s.lastStatus)}
                    {s.lastPeriodStart ? ` (week of ${s.lastPeriodStart})` : ""}
                  </div>
                </div>
                {s.nextStatuses.map((st) => (
                  <button
                    key={st}
                    type="button"
                    className="weekly-report-suggest-btn inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-sky-400/40 bg-sky-500/15 hover:bg-sky-500/25 text-[11px] font-semibold text-sky-100 transition"
                    onClick={() => onQuickAdd(s, st)}
                  >
                    <Plus className="w-3 h-3" />
                    {statusLabel(st)}
                  </button>
                ))}
              </li>
            ))}
          </ul>
        </section>
      )}
    </WrModal>
  );
};

export const AnotherStudyModal = ({ open, onAnother, onFinish, onClose }) => (
  <WrModal
    open={open}
    onClose={onClose}
    icon={FlaskConical}
    title="Add another study?"
    footer={
      <>
        <button type="button" onClick={onFinish} className={`${BTN_SOFT} mr-auto`}>
          No, review my report
        </button>
        <button type="button" onClick={onAnother} className={BTN_PRIMARY}>
          <Plus className="w-4 h-4" />
          Add another study
        </button>
      </>
    }
  >
    <p className="text-sm text-slate-300">
      Did you work on another study this week? Add it now, or review your report and submit it. Your progress is saved
      either way.
    </p>
  </WrModal>
);
